import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaRulerCombined, FaChevronLeft, FaChevronRight, FaWhatsapp, FaHeart, FaRegHeart, FaInfoCircle, FaShareAlt, FaCheck } from 'react-icons/fa';
import { getPropertyListings, toggleBookmark, checkBookmark } from '../services/api';
import PropertyImage from './PropertyImage';
import { useWhatsAppSettings, generateWhatsAppLink } from '../hooks/useWhatsAppSettings';

/* ── Reusable share helper ── */
const useShare = () => {
  const [copied, setCopied] = useState(false);
  const share = async ({ title, text, url }) => {
    if (navigator.share) {
      try { await navigator.share({ title, text, url }); } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return { share, copied };
};

const PropertyCard = ({ property, user, onLoginClick }) => {
  const [expanded, setExpanded] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const { share, copied } = useShare();
  const { settings } = useWhatsAppSettings();
  const slug = property.slug || property.name.toLowerCase().replace(/\s+/g, '-');
  const builderSlug = property.builderSlug || property.builder.toLowerCase().replace(/\s+/g, '-');
  const locationSlug = property.locationSlug || property.location.toLowerCase().replace(/\s+/g, '-');

  // Check bookmark status on mount
  useEffect(() => {
    if (user && property._id) {
      checkBookmark(property._id)
        .then(res => {
          if (res.isBookmarked !== undefined) {
            setBookmarked(res.isBookmarked);
          }
        })
        .catch(() => {});
    }
  }, [user, property._id]);

  const handleBookmarkClick = async () => {
    if (!user) {
      if (onLoginClick) onLoginClick();
      return;
    }
    
    if (bookmarkLoading) return;
    
    setBookmarkLoading(true);
    try {
      const response = await toggleBookmark(property._id);
      if (response.success) {
        setBookmarked(response.isBookmarked);
      }
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
    } finally {
      setBookmarkLoading(false);
    }
  };

  return (
    <div className="min-w-[340px] max-w-[380px] bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden flex-shrink-0 border border-gray-100">
      {/* Image */}
      <div className="relative h-52 overflow-hidden">
        <Link to={`/projects/${slug}`}>
          <PropertyImage
            property={property}
            className="w-full h-52 overflow-hidden"
            imgClassName="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          />
        </Link>
        {property.isNew && (
          <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            New Launch
          </span>
        )}
        {/* Action icons: Share + Bookmark */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5">
          {/* Share */}
          <button
            onClick={(e) => { e.preventDefault(); share({ title: property.name, text: `Check out ${property.name} by ${property.builder}`, url: `${window.location.origin}/projects/${slug}` }); }}
            className="bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white transition"
            title="Share Property"
          >
            {copied ? <FaCheck className="text-green-500 text-sm" /> : <FaShareAlt className="text-gray-600 text-sm" />}
          </button>
          {/* Bookmark */}
          <button
            onClick={handleBookmarkClick}
            disabled={bookmarkLoading}
            className="bg-white/90 backdrop-blur-sm rounded-full p-2 cursor-pointer hover:bg-white transition disabled:opacity-50"
          >
            {bookmarked ? (
              <FaHeart className="text-red-500 text-sm" />
            ) : (
              <FaRegHeart className="text-gray-500 text-sm" />
            )}
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
          <Link to={`/projects/${slug}`} className="text-white font-bold text-lg hover:text-red-300 transition">
            {property.name}
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Price */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-red-500 font-bold text-lg">{property.price}</p>
            <p className="text-gray-400 text-xs">{property.priceLabel}</p>
          </div>
        </div>

        {/* Builder + Location Row */}
        <div className="flex items-center justify-between mb-3">
          <Link to={`/builder/${builderSlug}`} className="flex items-center space-x-2 hover:text-red-500 transition group">
            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-bold text-gray-600">
                {property.builder.charAt(0)}
              </span>
            </div>
            <span className="text-sm text-gray-600 group-hover:text-red-500">{property.builder}</span>
          </Link>
          <Link to={`/in/${(property.city || 'mumbai').toLowerCase()}/${locationSlug}`} className="flex items-center space-x-1 text-xs text-gray-500 hover:text-red-500 transition">
            <FaMapMarkerAlt className="text-red-400" />
            <span>{property.location}</span>
          </Link>
        </div>

        {/* Area */}
        <div className="flex items-center text-xs text-gray-500 mb-3 bg-gray-50 rounded-xl p-3">
          <FaRulerCombined className="text-gray-400 mr-2" />
          <span>{property.area}</span>
        </div>

        {/* Configs */}
        <div className={`space-y-1.5 ${expanded ? '' : 'max-h-[72px] overflow-hidden'}`}>
          {property.configs.map((config, i) => (
            <div key={`${property.id}-config-${config.type}-${i}`} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-1.5">
              <span className="font-medium text-gray-700">{config.type}</span>
              <span className="text-gray-500">{config.size}</span>
              <span className="font-semibold text-gray-800">{config.price}</span>
            </div>
          ))}
        </div>

        {property.configs.length > 2 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-red-500 text-xs font-medium mt-2 hover:underline"
          >
            {expanded ? 'Show less' : `+${property.configs.length - 2} more configs`}
          </button>
        )}

        {/* Actions */}
        <div className="flex items-center space-x-2 mt-4">
          <Link
            to={`/projects/${slug}`}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold transition flex items-center justify-center space-x-1"
          >
            <FaInfoCircle className="text-xs" />
            <span>View Details</span>
          </Link>
          <a
            href={generateWhatsAppLink(settings.whatsappNumber, `Need details about ${property.name}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2.5 rounded-xl text-sm font-semibold transition flex items-center justify-center space-x-1"
          >
            <FaWhatsapp />
            <span>Live Chat</span>
          </a>
        </div>
      </div>
    </div>
  );
};

const PropertyListings = ({ user, onLoginClick }) => {
  const scrollRef = useRef(null);
  const [propertyListings, setPropertyListings] = useState([]);

  useEffect(() => {
    getPropertyListings().then(data => setPropertyListings(data || []));
  }, []);



  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 380, behavior: 'smooth' });
    }
  };

  return (
    <section id="properties" className="py-10 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <span className="text-red-500 text-sm font-semibold uppercase tracking-wider">PROPERTIES</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1">
            Top New Launches In Mumbai
          </h2>
          <p className="text-gray-500 mt-2">Discover the Latest Real Estate Projects in Mumbai</p>
        </div>

        <div className="relative">
          <button
            onClick={() => scroll(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:bg-red-50 transition"
          >
            <FaChevronLeft className="text-gray-600" />
          </button>

          <div
            ref={scrollRef}
            className="flex space-x-5 overflow-x-auto pb-4 scroll-container"
          >
            {propertyListings.map((property) => (
              <PropertyCard key={property._id || property.id} property={property} user={user} onLoginClick={onLoginClick} />
            ))}
          </div>

          <button
            onClick={() => scroll(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:bg-red-50 transition"
          >
            <FaChevronRight className="text-gray-600" />
          </button>
        </div>

      </div>
    </section>
  );
};

export default PropertyListings;
